import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosCheckbox,
    SovosIconButton,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, Close, ExpandMore, FilterList } from "mosaic-react-icons";
import {
    buildTranslationActionTooltip,
    buildTranslationRuleKey,
} from "../../../StagingDataPanel/aiFixApplication";

const AI_ACCENT = "secondary.main";
const AI_ACCENT_BG = "secondary.light";
const RULE_DETAILS_BG = "grey.100";
const PANEL_WIDTH = 395;

const SKIP_STATUSES = new Set(["skipped_non_deterministic", "skipped_non_nullable", "skipped_bad_format"]);

function deriveErrorColumns(issueSummary) {
    if (!issueSummary || typeof issueSummary !== "object") return [];
    const byColumn = issueSummary.by_column;
    if (!byColumn || typeof byColumn !== "object") return [];
    return Object.entries(byColumn)
        .filter(([, v]) => (v.errors ?? 0) > 0)
        .sort(([, a], [, b]) => (b.errors ?? 0) - (a.errors ?? 0))
        .map(([col]) => col);
}

function buildRules(aiFixes, acceptedFixes, rejectedFixes, visibleFixKeys) {
    if (!aiFixes?.suggestions) return [];
    return aiFixes.suggestions.map((col, idx) => {
        const visibleFixes = (col.fixes || []).filter(
            (f) => {
                const key = `${f.clb_id}:${col.column_name}`;
                return !rejectedFixes.has(key)
                    && (!visibleFixKeys || visibleFixKeys.has(key));
            },
        );
        const pendingFixes = visibleFixes.filter(
            (f) => f.status === "applied"
                && !f.requires_review
                && !acceptedFixes.has(`${f.clb_id}:${col.column_name}`),
        );
        const exampleFix = visibleFixes.find((f) => f.status === "applied") ?? null;
        const isManual = visibleFixes.every(
            (f) => SKIP_STATUSES.has(f.status) || f.requires_review,
        );
        const confidence = col.confidence != null ? Math.round(col.confidence * 100) : null;
        const totalAffected = col.total_affected ?? visibleFixes.length;

        return {
            index: idx + 1,
            columnName: col.column_name,
            fixMode: col.fix_mode ?? null,
            confidence,
            exampleFix,
            isManual,
            pendingFixes,
            visibleFixes,
            totalAffected,
            rationale: col.rationale,
            sampled: col.sampled || [],
        };
    }).filter((r) => r.visibleFixes.length > 0);
}

function buildTranslationRules(aiFixes, acceptedFixes, rejectedFixes, translationRuleGroups) {
    if (!aiFixes?.suggestions || !translationRuleGroups) return [];
    return translationRuleGroups.map((group, idx) => {
        const col = aiFixes.suggestions.find(
            (suggestion) => suggestion.column_name === group.columnName,
        );
        const groupFixKeys = new Set(
            group.fixes.map((fix) => `${fix.clbId}:${fix.columnName}`),
        );
        const visibleFixes = (col?.fixes || []).filter((fix) => {
            const key = `${fix.clb_id}:${group.columnName}`;
            return groupFixKeys.has(key) && !rejectedFixes.has(key);
        });
        const pendingFixes = visibleFixes.filter(
            (fix) => fix.status === "applied"
                && !fix.requires_review
                && !acceptedFixes.has(`${fix.clb_id}:${group.columnName}`),
        );

        return {
            index: idx + 1,
            columnName: group.columnName,
            fixMode: "translation",
            confidence:
                col?.confidence != null ? Math.round(col.confidence * 100) : null,
            exampleFix: visibleFixes.find((fix) => fix.status === "applied") ?? null,
            isManual: false,
            pendingFixes,
            rationale: col?.rationale,
            sampled: col?.sampled || [],
            selectionKey: buildTranslationRuleKey(group),
            totalAffected: visibleFixes.length,
            visibleFixes,
        };
    }).filter((rule) => rule.visibleFixes.length > 0);
}

const ConfidenceBadge = ({ confidence }) => {
    if (confidence == null) return null;
    const color = confidence >= 80 ? AI_ACCENT : confidence >= 60 ? "warning.dark" : "text.secondary";
    const bg = confidence >= 80 ? AI_ACCENT_BG : confidence >= 60 ? "warning.light" : "grey.100";
    return (
        <SovosBox
            sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 24,
                px: 1,
                borderRadius: 999,
                bgcolor: bg,
            }}
        >
            <SovosTypography
                variant="caption"
                sx={{ fontSize: 12, fontWeight: 600, color, lineHeight: 1 }}
            >
                {confidence}% confidence
            </SovosTypography>
        </SovosBox>
    );
};

function buildCardDetails(rule, cardType) {
    if (cardType === "manual" || !rule.exampleFix) {
        return [
            `Review staging values in column "${rule.columnName}"`,
            "No deterministic automatic correction is available",
            "Correct the affected values manually before continuing",
        ].join("\n");
    }

    const count = rule.pendingFixes.length || rule.totalAffected;
    if (cardType === "translation") {
        return [
            `Target column: ${rule.columnName}`,
            `Reusable exact-match translation: "${rule.exampleFix.original_value ?? "empty"}" → "${rule.exampleFix.new_value ?? "empty"}"`,
            `Apply to ${count} matching staging row${count !== 1 ? "s" : ""}`,
        ].join("\n");
    }

    return [
        `Target column: ${rule.columnName}`,
        `Replace "${rule.exampleFix.original_value ?? "empty"}" with "${rule.exampleFix.new_value ?? "empty"}"`,
        `Patch ${count} staging cell${count !== 1 ? "s" : ""} by row identifier`,
    ].join("\n");
}

function getCardCopy(cardType, index, count) {
    if (cardType === "translation") {
        return {
            title: `Translation #${index}`,
            detailsTitle: "Translation rule",
            summary: `Affects ${count} matching row${count !== 1 ? "s" : ""}`,
        };
    }
    if (cardType === "direct") {
        return {
            title: `Direct fix #${index}`,
            detailsTitle: "Direct cell fixes",
            summary: `Applies directly to ${count} cell${count !== 1 ? "s" : ""}`,
        };
    }
    return {
        title: `Manual fix #${index}`,
        detailsTitle: "Why manual review is required",
        summary: `Requires review for ${count} row${count !== 1 ? "s" : ""}`,
    };
}

const RuleCard = ({
    rule,
    expanded,
    onApprove,
    onReject,
    onToggle,
    acceptedFixes,
    canApply,
    onSelectionChange,
    selectable,
    selected,
    supportsTranslationApply = true,
}) => {
    const allApproved =
        rule.pendingFixes.length === 0 &&
        rule.visibleFixes.some((f) => acceptedFixes.has(`${f.clb_id}:${rule.columnName}`));
    const applicableFixCount = rule.pendingFixes.length;
    const hasTranslatableValue =
        rule.exampleFix?.original_value != null &&
        String(rule.exampleFix.original_value).length > 0;
    const isTranslationSuggestion =
        (rule.fixMode === "translation" ||
            (rule.fixMode !== "direct" && applicableFixCount >= 2)) &&
        hasTranslatableValue;
    const isBulkTranslationRule = supportsTranslationApply && isTranslationSuggestion;
    const cardType = rule.isManual ? "manual" : isTranslationSuggestion ? "translation" : "direct";
    const cardDetails = buildCardDetails(rule, cardType);
    const actionTooltip = isBulkTranslationRule
        ? buildTranslationActionTooltip({
            columnName: rule.columnName,
            originalValue: rule.exampleFix.original_value,
            newValue: rule.exampleFix.new_value,
            rowCount: applicableFixCount || rule.totalAffected,
        })
        : (rule.rationale || `Suggested normalization for "${rule.columnName}".`);

    const affectingCount = allApproved
        ? rule.visibleFixes.length
        : cardType === "manual"
          ? rule.totalAffected
          : applicableFixCount;
    const cardCopy = getCardCopy(cardType, rule.index, affectingCount);

    return (
        <SovosBox
            sx={{
                bgcolor: "common.white",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 1,
            }}
        >
            <SovosBox
                aria-expanded={expanded}
                aria-label={`${expanded ? "Collapse" : "Expand"} ${cardCopy.title}`}
                onClick={onToggle}
                onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onToggle();
                    }
                }}
                role="button"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    pt: 2,
                    pb: selectable ? 0 : 2,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { bgcolor: "grey.50" },
                }}
                tabIndex={0}
            >
                <SovosBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                        height: 32,
                    }}
                >
                    <SovosTypography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ fontSize: 16, lineHeight: "28px", color: "text.primary" }}
                    >
                        {cardCopy.title}
                    </SovosTypography>
                    <ConfidenceBadge confidence={rule.confidence} />
                    <SovosBox sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                        <SovosBox
                            sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 1,
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                            }}
                        >
                            <FilterList sx={{ fontSize: 18 }} />
                        </SovosBox>
                        <SovosBox
                            sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 1,
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                            }}
                        >
                            <ExpandMore
                                sx={{
                                    fontSize: 20,
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 150ms",
                                }}
                            />
                        </SovosBox>
                    </SovosBox>
                </SovosBox>

                {rule.exampleFix && !rule.isManual && (
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                            mt: 2,
                            height: 40,
                        }}
                    >
                        <SovosTypography
                            fontWeight={600}
                            sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            &ldquo;{rule.exampleFix.original_value ?? "—"}&rdquo;
                        </SovosTypography>
                        <SovosBox
                            aria-hidden
                            sx={{
                                flex: 1,
                                position: "relative",
                                height: 2,
                                bgcolor: "text.secondary",
                                opacity: 0.4,
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: 0,
                                    height: 0,
                                    borderTop: "5px solid transparent",
                                    borderBottom: "5px solid transparent",
                                    borderLeft: "7px solid",
                                    borderLeftColor: "text.secondary",
                                    opacity: 1,
                                },
                            }}
                        />
                        <SovosTypography
                            fontWeight={600}
                            sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                            }}
                        >
                            &ldquo;{rule.exampleFix.new_value ?? "—"}&rdquo;
                        </SovosTypography>
                    </SovosBox>
                )}

                {rule.isManual && (
                    <SovosTypography
                        sx={{ mt: 2, color: "text.secondary", fontStyle: "italic", fontSize: 14 }}
                    >
                        Manual correction required
                    </SovosTypography>
                )}

                <SovosTypography
                    sx={{
                        mt: 1.5,
                        color: "text.secondary",
                        fontSize: 14,
                        lineHeight: "20px",
                        width: "100%",
                    }}
                >
                    {cardCopy.summary}
                </SovosTypography>
            </SovosBox>
            {selectable && (
                <SovosBox
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        mt: 1.5,
                        px: 2,
                        pb: 2,
                    }}
                >
                    <SovosCheckbox
                        checked={selected}
                        inputProps={{ "aria-label": `Accept Translation #${rule.index}` }}
                        onChange={(event) => {
                            onSelectionChange(rule.selectionKey, event.target.checked);
                        }}
                    />
                    <SovosTypography
                        sx={{ color: "text.primary", fontWeight: 600 }}
                        variant="body2"
                    >
                        Accept
                    </SovosTypography>
                </SovosBox>
            )}

            {expanded && (
                <SovosBox sx={{ borderTop: "1px solid", borderColor: "divider", px: 2, py: 1.5, bgcolor: "common.white" }}>
                    <SovosTypography variant="caption" fontWeight={700} sx={{ color: "text.primary", display: "block", mb: 0.75 }}>
                        {cardCopy.detailsTitle}
                    </SovosTypography>
                    <SovosBox
                        sx={{
                            bgcolor: RULE_DETAILS_BG,
                            borderRadius: 1,
                            px: 1.25,
                            py: 1,
                            mb: 2,
                            whiteSpace: "pre-line",
                        }}
                    >
                        <SovosTypography variant="caption" sx={{ color: "text.secondary", fontSize: 11, lineHeight: 1.5 }}>
                            {cardDetails}
                        </SovosTypography>
                    </SovosBox>

                    <SovosTypography variant="caption" fontWeight={700} sx={{ color: "text.primary", display: "block", mb: 0.75 }}>
                        AI Reasoning
                    </SovosTypography>
                    <SovosTypography variant="caption" sx={{ color: "text.secondary", fontSize: 11, lineHeight: 1.5, display: "block", mb: 2 }}>
                        {rule.rationale || `Suggested normalization for "${rule.columnName}" based on the sampled values in staging.`}
                    </SovosTypography>

                    <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <SovosButton
                            size="small"
                            variant="text"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReject(rule.columnName, rule.visibleFixes);
                            }}
                        >
                            Reject
                        </SovosButton>
                        {!rule.isManual && applicableFixCount > 0 && (
                            <SovosTooltip title={actionTooltip} placement="top">
                                <span>
                                    <SovosButton
                                        size="small"
                                        variant="contained"
                                        disabled={!canApply}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onApprove(rule.columnName, rule.visibleFixes);
                                        }}
                                    >
                                        {isBulkTranslationRule
                                            ? "Create translation"
                                            : `Apply ${applicableFixCount} fix${applicableFixCount !== 1 ? "es" : ""}`}
                                    </SovosButton>
                                </span>
                            </SovosTooltip>
                        )}
                    </SovosBox>
                </SovosBox>
            )}
        </SovosBox>
    );
};

const AiFixPanel = ({
    acceptedFixes,
    aiFixes,
    bulkActionLabel,
    canApply = true,
    issueSummary,
    loadingAiFixes,
    onBulkAcceptSafe,
    onBulkApproveColumn,
    onRejectRule,
    onRequestFixes,
    onClose,
    rejectedFixes,
    selectionEnabled = false,
    supportsTranslationApply = true,
    title = "Fix Suggestions",
    translationRuleGroups,
    visibleFixKeys,
}) => {
    const errorColumns = useMemo(() => deriveErrorColumns(issueSummary), [issueSummary]);
    const [expandedRules, setExpandedRules] = useState(new Set());

    const handleDirectTrigger = useCallback(() => {
        onRequestFixes(errorColumns.length > 0 ? errorColumns : undefined);
    }, [onRequestFixes, errorColumns]);

    const handleToggleRule = useCallback((columnName) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            if (next.has(columnName)) next.delete(columnName);
            else next.add(columnName);
            return next;
        });
    }, []);

    const rules = useMemo(
        () => translationRuleGroups
            ? buildTranslationRules(
                aiFixes,
                acceptedFixes,
                rejectedFixes,
                translationRuleGroups,
            )
            : buildRules(aiFixes, acceptedFixes, rejectedFixes, visibleFixKeys),
        [
            acceptedFixes,
            aiFixes,
            rejectedFixes,
            translationRuleGroups,
            visibleFixKeys,
        ],
    );
    const selectableRuleKeys = useMemo(
        () => rules
            .filter((rule) => rule.pendingFixes.length > 0)
            .map((rule) => rule.selectionKey),
        [rules],
    );
    const [selectedRuleKeys, setSelectedRuleKeys] = useState(new Set());

    useEffect(() => {
        if (selectionEnabled) {
            setSelectedRuleKeys(new Set(selectableRuleKeys));
        }
    }, [selectableRuleKeys, selectionEnabled]);

    const handleRuleSelectionChange = useCallback((ruleKey, checked) => {
        setSelectedRuleKeys((previous) => {
            const next = new Set(previous);
            if (checked) next.add(ruleKey);
            else next.delete(ruleKey);
            return next;
        });
    }, []);

    const totalApplicableFixes = useMemo(
        () => rules.reduce((acc, r) => acc + r.pendingFixes.length, 0),
        [rules],
    );

    const hasSuggestions = rules.length > 0;

    return (
        <SovosBox
            sx={{
                width: PANEL_WIDTH,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                alignSelf: "stretch",
                bgcolor: "common.white",
                borderLeft: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
            }}
        >
            <SovosBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: 48,
                    px: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                    bgcolor: "common.white",
                }}
            >
                <AutoAwesome sx={{ fontSize: 24, color: AI_ACCENT }} />
                <SovosTypography
                    variant="h6"
                    fontWeight={600}
                    sx={{ flex: 1, fontSize: 18, lineHeight: "28px" }}
                >
                    {title}{hasSuggestions ? ` (${rules.length})` : ""}
                </SovosTypography>
                {onClose && (
                    <SovosIconButton
                        aria-label="Close"
                        onClick={onClose}
                        sx={{
                            color: "text.secondary",
                            "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                        }}
                        tooltipText="Close"
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </SovosIconButton>
                )}
            </SovosBox>

            <SovosBox
                sx={{
                    flex: 1,
                    overflow: "auto",
                    bgcolor: "grey.50",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 2,
                }}
            >
                {!hasSuggestions && !loadingAiFixes && (
                    <SovosBox sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 3, py: 3 }}>
                        <AutoAwesome sx={{ fontSize: 40, color: "grey.300" }} />
                        <SovosTypography variant="body2" sx={{ color: "text.secondary", textAlign: "center", px: 2 }}>
                            Sovi will analyse your data errors and suggest corrections.
                        </SovosTypography>
                        {onRequestFixes && (
                            <SovosButton
                                variant="contained"
                                size="small"
                                disabled={loadingAiFixes || errorColumns.length === 0}
                                onClick={handleDirectTrigger}
                                sx={{ mt: 1 }}
                            >
                                {loadingAiFixes ? "Analysing…" : "Suggest corrections"}
                            </SovosButton>
                        )}
                    </SovosBox>
                )}

                {loadingAiFixes && !hasSuggestions && (
                    <SovosBox sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, px: 3, py: 3 }}>
                        <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>Analysing data…</SovosTypography>
                    </SovosBox>
                )}

                {hasSuggestions && rules.map((rule) => (
                    <RuleCard
                        key={rule.selectionKey || rule.columnName}
                        rule={rule}
                        expanded={expandedRules.has(rule.selectionKey || rule.columnName)}
                        onToggle={() => handleToggleRule(rule.selectionKey || rule.columnName)}
                        onApprove={onBulkApproveColumn}
                        acceptedFixes={acceptedFixes}
                        canApply={canApply}
                        onReject={onRejectRule}
                        onSelectionChange={handleRuleSelectionChange}
                        selectable={selectionEnabled}
                        selected={selectedRuleKeys.has(rule.selectionKey)}
                        supportsTranslationApply={supportsTranslationApply}
                    />
                ))}
            </SovosBox>

            {hasSuggestions && (
                <SovosBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "flex-end",
                        height: 72,
                        px: 3,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                        bgcolor: "common.white",
                    }}
                >
                    {selectionEnabled && (
                        <SovosButton
                            color="primary"
                            disabled={!canApply || selectableRuleKeys.length === 0}
                            onClick={() =>
                                onBulkAcceptSafe(new Set(selectableRuleKeys))
                            }
                            size="small"
                            variant="text"
                        >
                            Accept All
                        </SovosButton>
                    )}
                    <SovosButton
                        variant="contained"
                        disabled={
                            !canApply
                            || (selectionEnabled
                                ? selectedRuleKeys.size === 0
                                : totalApplicableFixes === 0)
                        }
                        onClick={() =>
                            onBulkAcceptSafe(
                                selectionEnabled ? selectedRuleKeys : undefined,
                            )
                        }
                        sx={{ fontWeight: 600, height: 32, px: 2 }}
                    >
                        {selectionEnabled
                            ? `Accept rules (${selectedRuleKeys.size})`
                            : bulkActionLabel || `Apply all fixes (${totalApplicableFixes})`}
                    </SovosButton>
                </SovosBox>
            )}
        </SovosBox>
    );
};

export default AiFixPanel;
